import clsx from 'clsx'
import { Link } from 'expo-router'
import type { PropsWithChildren, ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { Platform, Pressable, View } from 'react-native'

import { Button } from '@/src/elements/button/default'
import { Card, CardBody } from '@/src/elements/card'
import { Chip } from '@/src/elements/chip'
import { Image } from '@/src/elements/image'
import { Icon } from '@/src/elements/media/Icon'
import { Typography } from '@/src/elements/text/Typography'
import { ICON } from '@/src/general/enums/icons'
import { getStrictDiffInDays } from '@/src/general/primitives/dates/dayjs'
import { getImageUrl } from '@/src/general/utils/getImageUrl'
import { useTranslate } from '@/src/lang/useTranslate'

import { COMMUNITY_ACTIVITY, COMMUNITY_LIFETIME } from '../../../models/community.constants'
import type { TCommunityCardBaseProps } from './CommunityCard.types'

const TransparentChip = ({ children, icon }: PropsWithChildren<{ icon: ICON }>) => (
  <Typography
    colors="default"
    contrast="low"
    size={typeof children === 'number' ? 'md' : 'sm'}
    className="flex flex-row"
  >
    <Icon name={icon} size="sm" className="self-center mr-1" />
    {children}
  </Typography>
)

export const CommunityCardBaseV2 = ({
  className,
  community,
  community: { name, nbMembers, picture, fromDate, toDate },
  onCardPress,
  onSharePress,
  onSubscribePress,
  isLoading,
  fullWidth,
  showSubscribeButton = true,
  testId,
  href,
}: TCommunityCardBaseProps): ReactNode => {
  const hasDates = fromDate && toDate
  const displayShareButton = community.hasJoined && onSharePress
  const { t } = useTranslate()

  const showFutureDate =
    community.activity === COMMUNITY_ACTIVITY.COMING_SOON &&
    community.lifetime !== COMMUNITY_LIFETIME.EVERLASTING &&
    hasDates &&
    getStrictDiffInDays(new Date(), fromDate) > 0

  return (
    <Card
      testID={testId}
      additionalProps={{ role: 'link' }}
      onPress={onCardPress}
      isPressable={!!onCardPress}
      shadow="none"
      className={clsx(
        'native:w-[220px] native:h-[335px] native:max-h-[335px] native:max-w-[220px] min-w-[130px] max-w-[200px] flex-1 bg-white',
        fullWidth && 'native:min-w-full max-w-full',
        className,
      )}
    >
      <View className={clsx('relative h-[137px]', { 'native:min-w-full max-w-full': fullWidth })}>
        <Link href={href} asChild>
          <Pressable className="h-full w-full">
            <Image contentFit="cover" className="flex" source={getImageUrl(picture)} />
          </Pressable>
        </Link>
        <View className="flex max-w-full flex-row items-start absolute bottom-0 translate-y-1/2 px-2">
          {community.isPrivate && (
            <Chip text={t('community.private.private')} iconName={ICON.INCOGNITO} colors="default" variant="flat" />
          )}
        </View>
      </View>
      <CardBody className="items-left flex flex-col p-0 py-2">
        <Link href={href} asChild>
          <Pressable className="flex-1 flex-col gap-2">
            <Typography
              bold
              colors="default"
              contrast="high"
              className="native:min-h-[56px]"
              numberOfLines={Platform.OS === 'web' ? 3 : 2}
            >
              {name}
            </Typography>
            {showFutureDate ? (
              <TransparentChip icon={ICON.CALENDAR}>
                <FormattedMessage
                  id="words.in-nb-days"
                  values={{ nbDays: Math.ceil(getStrictDiffInDays(new Date(), fromDate)) }}
                />
              </TransparentChip>
            ) : (
              <TransparentChip icon={ICON.USERS}>{nbMembers}</TransparentChip>
            )}
            {community.lifetime === COMMUNITY_LIFETIME.NOVENA && (
              <TransparentChip icon={ICON.PATH}>
                <FormattedMessage id="user.wizard.novena" />
              </TransparentChip>
            )}
            {community.lifetime === COMMUNITY_LIFETIME.RETREAT && hasDates && (
              <TransparentChip icon={ICON.PATH}>
                <FormattedMessage
                  id="words.nb-days"
                  values={{ nbDays: Math.ceil(getStrictDiffInDays(fromDate, toDate)) }}
                />
              </TransparentChip>
            )}
          </Pressable>
        </Link>
        {showSubscribeButton && (
          <Button
            variant="solid"
            colors={displayShareButton ? 'secondary' : 'primary'}
            fullWidth
            onPress={displayShareButton ? onSharePress : onSubscribePress}
            className="relative mt-auto flex flex-row items-center !justify-center"
            isLoading={isLoading}
          >
            {displayShareButton ? <FormattedMessage id="share.share" /> : <FormattedMessage id="words.i-subscribe" />}
          </Button>
        )}
      </CardBody>
    </Card>
  )
}
